/**
 * CALLBACK DE ACCOUNTS - Magic link / confirmación por email
 *
 * Cuando el usuario hace clic en el enlace del email, Supabase redirige aquí
 * con tokens en el hash (#access_token=...&refresh_token=...).
 * Establecemos sesión y ejecutamos el handoff SSO hacia la app de origen.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@shared/lib/supabaseClient';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { APP_SSO_CONFIGS } from '@shared/navigation/appSSOConfig';
import { handleHandoffRedirect, setSSOCookie } from './Login';

const AccountsAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const didRunRef = useRef(false);

  useEffect(() => {
    if (didRunRef.current) return;
    const hash = window.location.hash?.substring(1);
    if (!hash) {
      setStatus('error');
      setMessage('No se encontraron datos de sesión en el enlace.');
      return;
    }

    didRunRef.current = true;
    let app = searchParams.get('app');
    let returnTo = searchParams.get('returnTo');
    let clientOrigin = searchParams.get('clientOrigin');

    // Respaldo: si Google/Supabase no conservaron query en la URL, leer de sessionStorage
    if (!app || !returnTo || !clientOrigin) {
      try {
        const stored = sessionStorage.getItem('sso_oauth_app_return');
        if (stored) {
          const parsed = JSON.parse(stored) as { app?: string; returnTo?: string; clientOrigin?: string };
          const savedApp = (parsed.app && parsed.app.trim()) || null;
          const savedReturnTo = (parsed.returnTo && parsed.returnTo.trim()) || null;
          const savedCo = (parsed.clientOrigin && parsed.clientOrigin.trim()) || null;
          if (savedApp && !app) app = savedApp;
          if (savedReturnTo && !returnTo) returnTo = savedReturnTo;
          if (savedCo && !clientOrigin) clientOrigin = savedCo;
          sessionStorage.removeItem('sso_oauth_app_return');
        }
      } catch (_) {}
    }

    if (!app || !APP_SSO_CONFIGS[app]) {
      setStatus('error');
      setMessage(`App "${app || 'desconocida'}" no soportada.`);
      return;
    }

    const run = async () => {
      const hashParams = new URLSearchParams(hash);
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const errorDesc = hashParams.get('error_description') || hashParams.get('error');

      if (errorDesc) {
        setStatus('error');
        setMessage(errorDesc);
        return;
      }
      if (!accessToken || !refreshToken) {
        setStatus('error');
        setMessage('Enlace inválido o expirado. Solicita uno nuevo desde el login.');
        return;
      }

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (sessionError) {
        setStatus('error');
        setMessage('Error al establecer sesión: ' + sessionError.message);
        return;
      }

      const session = (await supabase.auth.getSession()).data.session;
      if (session?.access_token) {
        try {
          const { notifyCoreAPILogin } = await import('@shared/services/coreAuthService');
          await notifyCoreAPILogin(session.access_token);
        } catch (_) {}
        await setSSOCookie(session.access_token);
      }

      const trace = createSsoTrace({ app, feature: 'MAGIC_LINK_CALLBACK' });
      const ok = await handleHandoffRedirect(
        app,
        returnTo,
        (err) => {
          setStatus('error');
          setMessage(err);
        },
        trace,
        clientOrigin,
      );
      if (!ok) return;
      setStatus('success');
    };

    run();
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <p className="text-muted-foreground">Completando inicio de sesión...</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="text-center max-w-md">
          <p className="text-destructive font-medium">{message}</p>
          <a href="/login" className="text-primary underline mt-4 inline-block">
            Volver al login
          </a>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <p className="text-muted-foreground">Redirigiendo a la aplicación...</p>
    </div>
  );
};

export default AccountsAuthCallback;
